import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, Typography, Button, Box, Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '../context/SessionContext';
import { usePortalAuth } from '../context/PortalAuthContext';
import { contentApi } from '../utils/api';

interface ScratchReminder {
  title: string;
  message: string;
  explanation: string;
  email: string;
  subject: string;
  button_text: string;
}

export default function CompletePage() {
  const { session, endSession } = useSession();
  const courseLevel = session?.assessment_course_level?.trim() || 'Precalculus I';
  const { user } = usePortalAuth();
  const navigate = useNavigate();
  const { data: reminder } = useQuery<ScratchReminder>({
    queryKey: ['content', 'scratch-reminder'],
    queryFn: () => contentApi.getScratchReminder(),
  });

  const handleEmailScratchWork = () => {
    if (reminder) {
      const mailtoUrl = `mailto:${reminder.email}?subject=${encodeURIComponent(reminder.subject)}`;
      window.open(mailtoUrl, '_blank');
    }
  };

  const handleFinish = async () => {
    await endSession();

    if (user?.role === 'student') {
      navigate('/student-portal');
      return;
    }

    // Close the browser tab
    window.close();
  };

  return (
    <Card>
      <CardContent sx={{ p: 4, textAlign: 'center' }}>
        <Box sx={{ mb: 3 }}>
          <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main' }} />
        </Box>

        <Typography variant="h4" component="h1" gutterBottom color="primary">
          Assessment Complete!
        </Typography>

        <Typography variant="body1" paragraph sx={{ maxWidth: 500, mx: 'auto' }}>
          {`Congratulations on completing the ${courseLevel} Diagnostic Assessment.
          Your personalized feedback and PDF have been generated.`}
        </Typography>

        {reminder && (
          <Alert
            severity="warning"
            sx={{
              maxWidth: 500,
              mx: 'auto',
              my: 3,
              textAlign: 'left',
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {reminder.title}
            </Typography>
            <Typography variant="body2" paragraph>
              {reminder.message}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {reminder.explanation}
            </Typography>
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={2} alignItems="center" sx={{ mt: 4 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={handleEmailScratchWork}
            startIcon={<EmailIcon />}
            sx={{ minWidth: 280 }}
          >
            {reminder?.button_text || 'Email My Scratch Work'}
          </Button>

          <Button
            variant="contained"
            size="large"
            onClick={handleFinish}
            sx={{ minWidth: 280 }}
          >
            {user?.role === 'student' ? 'Back to Student Portal' : 'Finish'}
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
          {`Best wishes for your success in ${courseLevel}!`}
        </Typography>
      </CardContent>
    </Card>
  );
}
